"use client";

import Image from "next/image";
import React, { useState, useEffect } from "react";

export interface ProcessStep {
  title?: string;
  description?: string;
  image?: {
    secure_url?: string;
    public_id?: string;
    url?: string;
  };
}

interface ProcessSectionProps {
  steps: ProcessStep[];
  heading1?: string;
  heading1Color?: string;
  heading2?: string;
  heading2Color?: string;
  embedHtml?: string;
}

export default function ProcessSection({
  steps,
  heading1,
  heading1Color,
  heading2,
  heading2Color,
  embedHtml,
}: ProcessSectionProps) {
  const [activeIndices, setActiveIndices] = useState<Set<number>>(new Set());
  const [scrollDirection, setScrollDirection] = useState<"up" | "down">("down");
  const lastScrollYRef = React.useRef(0);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const direction = currentScrollY > lastScrollYRef.current ? "down" : "up";
          
          setScrollDirection(direction);
          lastScrollYRef.current = currentScrollY;

          if (direction === "down") {
            // On scroll down, find which steps are in view and open them
            const stepElements = document.querySelectorAll("[data-process-step]");
            const newActiveIndices = new Set<number>();
            
            stepElements.forEach((el, index) => {
              const rect = el.getBoundingClientRect();
              const isInView = rect.top < window.innerHeight * 0.8 && rect.bottom > 0;
              
              if (isInView) {
                newActiveIndices.add(index);
              }
            });
            
            setActiveIndices(newActiveIndices);
          } else {
            // On scroll up, close all
            setActiveIndices(new Set());
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    // Check on initial load
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const hasSteps = steps && steps.length > 0;
  
  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4">
        {hasSteps && (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* Left Column - Title */}
            <div className="lg:col-span-2 flex items-start">
              {(heading1 || heading2) ? (
                <h2 className="text-4xl font-bold leading-tight md:text-6xl lg:text-[65px] break-words">
                  {heading1 && (
                    <span style={{ color: heading1Color || "#3b0764" }}>
                      {heading1.charAt(0).toUpperCase() + heading1.slice(1).toLowerCase()}
                    </span>
                  )}
                  {heading1 && heading2 && " "}
                  {heading2 && (
                    <span style={{ color: heading2Color || "#3b0764" }}>
                      {heading2.charAt(0).toUpperCase() + heading2.slice(1).toLowerCase()}
                    </span>
                  )}
                </h2>
              ) : (
                <h2 className="text-4xl font-bold leading-tight text-violet-950 md:text-6xl lg:text-[65px] break-words">
                  Process Steps
                </h2>
              )}
            </div>

            {/* Right Column - Process Steps Cards */}
            <div className="lg:col-span-1 space-y-6">
              {steps.map((step, index) => {
                const isActive = activeIndices.has(index);
                const isScrolledDown = scrollDirection === "down";
                
                return (
                  <div
                    key={`${step.title}-${index}`}
                    data-process-step={index}
                    className="relative flex gap-4 transition-all duration-300"
                    style={{ 
                      minHeight: isActive && isScrolledDown ? "160px" : "90px",
                      transitionDelay: isActive && isScrolledDown ? `${index * 150}ms` : `${(steps.length - 1 - index) * 150}ms`
                    }}
                  >
                    {/* Left Line */}
                    <div
                      className={`w-2 flex-shrink-0 rounded-full transition-colors duration-300 ${
                        isActive && isScrolledDown
                          ? "bg-violet-950"
                          : "bg-gray-300"
                      }`}
                    />

                    {/* Card Content */}
                    <div className="flex flex-1 flex-col justify-between pt-1">
                      {/* Title and Image in same row */}
                      <div className="mb-2 flex items-start gap-3">
                        {/* Title */}
                        {step.title && (
                          <h3 className="flex-1 max-w-[200px] text-xl font-semibold leading-tight text-violet-950 md:max-w-[250px] md:text-2xl">
                            {step.title}
                          </h3>
                        )}

                        {/* Image - Always show container */}
                        <div className="h-20 w-[145px] shrink-0 overflow-hidden rounded-2xl bg-gray-100">
                          {step.image && (step.image.secure_url || step.image.url || step.image.public_id) ? (
                            <Image
                              src={
                                step.image.secure_url ||
                                step.image.url ||
                                (step.image.public_id
                                  ? `https://res.cloudinary.com/dkaghqnvm/image/upload/${step.image.public_id}`
                                  : "")
                              }
                              alt={step.title || "Process step image"}
                              width={145}
                              height={80}
                              className="h-full w-full object-cover"
                              unoptimized
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                              No image
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Description - Animated at bottom */}
                      {step.description && (
                        <div
                          className={`mt-auto overflow-hidden transition-all duration-300 ease-in-out ${
                            isActive && isScrolledDown
                              ? "max-h-[120px] opacity-100"
                              : "max-h-0 opacity-0"
                          }`}
                          style={{
                            transitionDelay: isActive && isScrolledDown ? `${index * 150}ms` : `${(steps.length - 1 - index) * 150}ms`
                          }}
                        >
                          <p className="pb-1 text-base leading-relaxed text-gray-400 md:text-lg">
                            {step.description}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {embedHtml && (
          <div
            className={hasSteps ? "mt-10" : "mt-8"}
            dangerouslySetInnerHTML={{ __html: embedHtml }}
            suppressHydrationWarning
          />
        )}
      </div>
    </section>
  );
}
